/**
 * lib/SubscriptionContext.tsx
 * ----------------------------
 * What: React context exposing the current subscription tier and helper logic.
 * Why: Allows UI to easily gate features and render status without wiring props
 * through multiple layers.
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';
import { getPlatform } from '../app/utils/platform';
import { useTheme, type ThemeType } from './ThemeContext';

export type SubscriptionTier = 'free' | 'premium';
export type Currency = 'GBP' | 'USD' | 'EUR';
export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

export type SubscriptionContextType = {
  tier: SubscriptionTier | undefined;
  itemCount: number;
  roomCount: number;
  itemLimit: number;
  roomLimit: number;
  canAddItem: boolean;
  canAddRoom: boolean;
  refreshCounts: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  refreshSubscription: () => Promise<void>;
  currency: Currency;
  dateFormat: DateFormat;
  updateDisplayPreferences: (preferences: { currency?: Currency; date_format?: DateFormat }) => Promise<void>;
  themePreference: ThemeType;
  updateThemePreference: (pref: ThemeType) => Promise<void>;
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const FREE_TIER_LIMITS = {
  items: 5,
  rooms: 1,
};

const PREMIUM_TIER_LIMITS = {
  items: Infinity,
  rooms: Infinity,
};

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier | undefined>(undefined);
  const [itemCount, setItemCount] = useState(0);
  const [roomCount, setRoomCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [canAddItem, setCanAddItem] = useState(false);
  const [canAddRoom, setCanAddRoom] = useState(false);
  const [currency, setCurrency] = useState<Currency>('GBP');
  const [dateFormat, setDateFormat] = useState<DateFormat>('DD/MM/YYYY');
  const { setThemeType } = useTheme();
  const [themePreference, setThemePreference] = useState<ThemeType>('automatic');
  
  const isFetching = useRef<{ [key: string]: boolean }>({});
  const initialFetchDone = useRef(false);

  console.log('💳 [SubscriptionProvider] Provider rendered with state:', {
    tier,
    itemCount,
    roomCount,
    isLoading,
    hasError: !!error,
    canAddItem,
    canAddRoom,
    currency,
    dateFormat,
    hasUser: !!user,
    initialFetchDone: initialFetchDone.current
  });

  const itemLimit = tier === 'premium' ? PREMIUM_TIER_LIMITS.items : FREE_TIER_LIMITS.items;
  const roomLimit = tier === 'premium' ? PREMIUM_TIER_LIMITS.rooms : FREE_TIER_LIMITS.rooms;

  const fetchSubscription = useCallback(async () => {
    console.log('💳 [SubscriptionProvider] fetchSubscription called, user:', !!user, 'isFetching:', isFetching.current.subscription);
    if (!user || isFetching.current.subscription) {
      console.log('❌ [SubscriptionProvider] fetchSubscription early return');
      return;
    }
    isFetching.current.subscription = true;
    try {
      setError(null);
      console.log('💳 [SubscriptionProvider] Checking existing subscription for user:', user.id);
      // First, check if the user has a subscription record
      const { data: existingSubscription, error: checkError } = await supabase
        .from('accounts')
        .select('tier, currency, date_format, theme_preference')
        .eq('user_id', user.id)
        .maybeSingle();

      if (checkError) {
        console.error('❌ [SubscriptionProvider] Error checking subscription:', checkError);
        throw checkError;
      }

      if (!existingSubscription) {
        console.log('💳 [SubscriptionProvider] No subscription exists, creating free tier');
        // If no subscription exists, create a free tier subscription
        const { error: insertError } = await supabase
          .from('accounts')
          .insert([
            {
              user_id: user.id,
              tier: 'free',
              currency: 'GBP',
              date_format: 'DD/MM/YYYY',
              theme_preference: 'automatic',
              created_with: getPlatform(),
              updated_with: getPlatform(),
            }
          ]);

        if (insertError) {
          console.error('❌ [SubscriptionProvider] Error creating subscription:', insertError);
          throw insertError;
        }
        setTier('free');
        setCurrency('GBP');
        setDateFormat('DD/MM/YYYY');
        setThemePreference('automatic');
        setThemeType('automatic');
        console.log('✅ [SubscriptionProvider] Created free tier subscription');
      } else {
        console.log('💳 [SubscriptionProvider] Found existing subscription:', existingSubscription);
        setTier(existingSubscription.tier);
        setCurrency(existingSubscription.currency);
        setDateFormat(existingSubscription.date_format);
        // Apply theme from DB only if the local preference is still at the
        // default (automatic). This prevents an earlier fetch that returns
        // stale data from reverting a theme the user just changed.
        if (themePreference === 'automatic') {
          setThemePreference(existingSubscription.theme_preference);
          setThemeType(existingSubscription.theme_preference);
        }
      }

      // Update canAddRoom and canAddItem based on the new tier
      if (existingSubscription?.tier === 'premium') {
        console.log('💳 [SubscriptionProvider] Premium tier, setting unlimited permissions');
        setCanAddRoom(true);
        setCanAddItem(true);
      }
    } catch (error: any) {
      console.error('❌ [SubscriptionProvider] Error in fetchSubscription:', error);
      setError(error);
      setTier('free');
      setCurrency('GBP');
      setDateFormat('DD/MM/YYYY');
      setThemePreference('automatic');
      setThemeType('automatic');
    } finally {
      isFetching.current.subscription = false;
    }
  }, [user, themePreference]);

  const fetchCounts = useCallback(async () => {
    console.log('💳 [SubscriptionProvider] fetchCounts called, user:', !!user, 'isFetching:', isFetching.current.counts);
    if (!user || isFetching.current.counts) {
      console.log('❌ [SubscriptionProvider] fetchCounts early return');
      return;
    }
    isFetching.current.counts = true;
    try {
      setError(null);
      console.log('💳 [SubscriptionProvider] Fetching item and room counts');
      const [itemsResponse, roomsResponse] = await Promise.all([
        supabase
          .from('items')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('rooms')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
      ]);

      if (itemsResponse.error) {
        console.error('❌ [SubscriptionProvider] Error fetching items count:', itemsResponse.error);
        throw itemsResponse.error;
      }
      if (roomsResponse.error) {
        console.error('❌ [SubscriptionProvider] Error fetching rooms count:', roomsResponse.error);
        throw roomsResponse.error;
      }

      const newItemCount = itemsResponse.count || 0;
      const newRoomCount = roomsResponse.count || 0;
      
      console.log('💳 [SubscriptionProvider] Counts fetched:', { newItemCount, newRoomCount, tier });
      
      setItemCount(newItemCount);
      setRoomCount(newRoomCount);
      
      // Update canAddRoom and canAddItem based on the new counts and tier
      if (tier === 'premium') {
        console.log('💳 [SubscriptionProvider] Premium tier, setting permissions');
        setCanAddRoom(true);
        setCanAddItem(newRoomCount > 0);
      } else {
        console.log('💳 [SubscriptionProvider] Free tier, checking limits:', { roomLimit, itemLimit });
        setCanAddRoom(newRoomCount < roomLimit);
        setCanAddItem(newRoomCount > 0 && newItemCount < itemLimit);
      }
    } catch (error: any) {
      console.error('❌ [SubscriptionProvider] Error in fetchCounts:', error);
      setError(error);
      setItemCount(0);
      setRoomCount(0);
      setCanAddRoom(true);
      setCanAddItem(false);
    } finally {
      isFetching.current.counts = false;
    }
  }, [user, tier, roomLimit, itemLimit]);

  const refreshSubscription = useCallback(async () => {
    console.log('💳 [SubscriptionProvider] refreshSubscription called, user:', !!user);
    if (!user) return;
    setIsLoading(true);
    try {
      console.log('💳 [SubscriptionProvider] Refreshing subscription and counts');
      await Promise.all([fetchSubscription(), fetchCounts()]);
    } finally {
      setIsLoading(false);
    }
  }, [user, fetchSubscription, fetchCounts]);

  const updateDisplayPreferences = useCallback(async (preferences: { currency?: Currency; date_format?: DateFormat }) => {
    console.log('💳 [SubscriptionProvider] updateDisplayPreferences called:', preferences);
    if (!user) return;
    try {
      const { error } = await supabase
        .from('accounts')
        .update({
          ...preferences,
          updated_with: getPlatform(),
        })
        .eq('user_id', user.id);

      if (error) throw error;

      if (preferences.currency) setCurrency(preferences.currency);
      if (preferences.date_format) setDateFormat(preferences.date_format);
      console.log('✅ [SubscriptionProvider] Display preferences updated');
    } catch (error: any) {
      console.error('❌ [SubscriptionProvider] Error updating display preferences:', error);
      throw error;
    }
  }, [user]);

  const updateThemePreference = useCallback(async (pref: ThemeType) => {
    console.log('💳 [SubscriptionProvider] updateThemePreference called:', pref);
    // Update local theme immediately for responsiveness
    setThemeType(pref);
    setThemePreference(pref);

    // If user not logged in yet just persist locally via ThemeContext
    if (!user) return;

    try {
      const { error } = await supabase
        .from('accounts')
        .update({
          theme_preference: pref,
          updated_with: getPlatform(),
        })
        .eq('user_id', user.id);

      if (error) throw error;

      console.log('✅ [SubscriptionProvider] Theme preference updated in DB');
    } catch (error: any) {
      console.error('❌ [SubscriptionProvider] Error updating theme preference:', error);
    }
  }, [user, setThemeType]);

  // Initial fetch
  useEffect(() => {
    console.log('💳 [SubscriptionProvider] Initial fetch effect, user:', !!user, 'initialFetchDone:', initialFetchDone.current);
    if (user && !initialFetchDone.current) {
      initialFetchDone.current = true;
      console.log('💳 [SubscriptionProvider] Starting initial subscription refresh');
      refreshSubscription();
    }
  }, [user, refreshSubscription]);

  // Reset state when user changes
  useEffect(() => {
    if (!user) {
      initialFetchDone.current = false;
      setTier(undefined);
      setItemCount(0);
      setRoomCount(0);
      setCanAddRoom(true);
      setCanAddItem(false);
      setIsLoading(false);
      // Reset theme preference so that the next logged in user can have their
      // preference applied from the database. This avoids leaking the previous
      // user's preference into the next session.
      setThemePreference('automatic');
      // Do NOT reset the actual themeType here – we intentionally preserve the
      // last active theme for the logged-out state. The ThemeProvider will keep
      // the existing themeType so that the sign-in screen retains the same
      // look, but because themePreference is now back to the default, the first
      // subscription fetch for the next user will be allowed to overwrite it.
    }
  }, [user]);

  return (
    <SubscriptionContext.Provider
      value={{
        tier,
        itemCount,
        roomCount,
        itemLimit,
        roomLimit,
        canAddItem,
        canAddRoom,
        refreshSubscription,
        isLoading,
        error: error?.message || null,
        refreshCounts: refreshSubscription,
        currency,
        dateFormat,
        updateDisplayPreferences,
        themePreference,
        updateThemePreference,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
} 